import React, {
  createContext,
  createRef,
  MutableRefObject,
  useContext,
  useEffect,
  useState,
} from 'react'
import { StkBoosterTypes } from '../components/Savings/SavingsToolkit/Boosters/StkBoosters'
import { StkBooster, StkBucket, StkTab } from '../types'
import { useSafetynetModal } from './SafetynetModalProvider'
import { useStkInitialTab } from '../hooks/use-stk-inital-tab'

// The different views possible within the "Organize with Buckets" accordion
export type StkBucketView =
  | 'ADD'
  | 'ENTICING_VIEW'
  | 'MULTI_GOALS'
  | 'GOALS_EDIT'
  | 'SINGLE_GOAL'
  | 'DISTRIBUTE'
  | 'SETTINGS'
  | 'DETAILS'
  | 'TILES' // 'TILES' is the view shown when other views are "closed" using the "X"

// The possible views within the Buckets > SETTINGS section
export type BucketSettingsView = 'MONEY_OUT' | 'MONEY_IN' | 'INTEREST_PAID'
// The possible views within the Buckets > DISTRIBUTE section
export type BucketDistributeView = 'CHOOSE_TWO' | 'CHOOSE_MULTIPLE'

// The possible views within the "Optimize with Boosters" accordion
// Using 'FAS' acronym for consistency with the API.
// FAS (Find And Save) is pre-launch name for Surprise Savings
// SIFI (Set It and Forget It) is the pre-launch name for Recurring transfers
// "Sub-views" for a given booster MUST follow the format `${boosterType}_SUBVIEW`
// in order to work with the calculated `boosterType` prop below.
export type StkBoosterView =
  | 'TILES' // Booster Active/Explore tiles. Not to be confused with Round Ups tiles
  | 'SIFI_VIEW'
  | 'SIFI_ADD'
  | 'SIFI_EDIT'
  | 'RUP_VIEW'
  | 'RUP_EDIT'
  | 'RUP_ADD'
  | 'RUP_EPR'
  | 'FAS_VIEW'
  | 'FAS_ADD'
  | 'FAS_EDIT'
  | 'DEFAULT'

// The possible types of messages shown above bucket tiles view
export type BucketsMessage = 'DELETE' | 'ERROR'
// The possible types of messages shown in any booster view that should
// go away when the view changes
export type BoostersMessage =
  | 'GEN_ERROR'
  | 'FAS_ADD'
  | 'FAS_DELETE'
  | 'FAS_EDIT'
  | 'RUP_ADD'
  | 'RUP_EDIT'
  | 'RUP_STOP'
  | 'SIFI_ADD'
  | 'SIFI_EDIT'
  | 'SIFI_DELETE'
  | 'SIFI_ACCOUNT_RESTRICTION'
  | 'SIFI_STORE_AND_FORWARD'
  | 'EXTERNAL_ACCOUNTS_ADD'
  | 'EAV_IN_USE'
  | 'TRANSFER_SPEED_ERROR'
  | 'TRANSFER_AMOUNT_ERROR'

export type GoalsMessage =
  | 'GOAL_EDIT_ERROR'
  | 'GOAL_CREATE_ERROR'
  | 'GOAL_EDIT_SUCCESS'
  | 'GOAL_DELETE_SUCCESS'
  | 'GOAL_CREATE_SUCCESS'
  | 'SINGLE_GOAL_CREATE_SUCCESS'

export type SavingsToolkitRefs = {
  activityHeading: MutableRefObject<HTMLHeadingElement | null>
  bucketsHeading: MutableRefObject<HTMLHeadingElement | null>
  boostersHeading: MutableRefObject<HTMLHeadingElement | null>
  recurringTransfersHeading: MutableRefObject<HTMLDivElement | null>
}

export type BoosterButtonRefs = {
  SIFIButton: MutableRefObject<HTMLButtonElement | null>
  RUPButton: MutableRefObject<HTMLButtonElement | null>
  FASButton: MutableRefObject<HTMLButtonElement | null>
}

interface IStkViewState {
  selectedBucket: StkBucket | null
  selectedBooster: StkBooster | null
  bucketsView: StkBucketView
  boostersView: StkBoosterView
  bucketSettingsView: BucketSettingsView
  bucketDistributeView: BucketDistributeView
  editingBucket: boolean
  bucketsMessage: BucketsMessage | null
  boostersMessage: BoostersMessage | null
  goalsMessage: GoalsMessage | null
  safetynetReady: Record<'buckets' | 'boosters', boolean>
  selectedTab: StkTab
}

export interface StkViewProps extends IStkViewState {
  updateView: (updated: Partial<IStkViewState>) => void
  updateViewWithSafetynet: (
    updated: Partial<IStkViewState>,
    section: 'buckets' | 'boosters',
    proceedFocusHandler: () => void,
    tagPrefix?: string,
  ) => Promise<void>
  readySafetynet: (section: 'buckets' | 'boosters', ready?: boolean) => void
  boosterType: StkBoosterTypes | null
  refs: SavingsToolkitRefs
  boosterTileButtonRefs: BoosterButtonRefs
}

const refs: SavingsToolkitRefs = {
  activityHeading: createRef<HTMLHeadingElement>(),
  bucketsHeading: createRef<HTMLHeadingElement>(),
  boostersHeading: createRef<HTMLHeadingElement>(),
  recurringTransfersHeading: createRef<HTMLDivElement>(),
}

const boosterTileButtonRefs: BoosterButtonRefs = {
  SIFIButton: createRef<HTMLButtonElement>(),
  RUPButton: createRef<HTMLButtonElement>(),
  FASButton: createRef<HTMLButtonElement>(),
}

export const StkViewContext = createContext({} as StkViewProps)

export const StkViewProvider: React.FC = ({ children }) => {
  const { showSafetynetModal } = useSafetynetModal()
  const initialTab = useStkInitialTab()

  const [state, setState] = useState<IStkViewState>({
    selectedBucket: null,
    selectedBooster: null,
    bucketsView: 'TILES',
    boostersView: 'DEFAULT',
    bucketSettingsView: 'MONEY_OUT',
    bucketDistributeView: 'CHOOSE_TWO',
    editingBucket: false,
    bucketsMessage: null,
    boostersMessage: null,
    safetynetReady: { buckets: false, boosters: false },
    goalsMessage: null,
    selectedTab: initialTab,
  })

  // Logic to clear the boostersMessage when the boostersView changes.
  // This is being done inline in the update method instead of a useEffect
  // due to difficulty in managing the timing/states correctly. This pattern requires
  // changes to the boostersMessage be completed in the same "updateView" call as the
  // boosterSection
  const clearBoostersMessage = (
    updated: Partial<IStkViewState>,
  ): Partial<IStkViewState> => {
    // never clear if a new message has been specified
    if (updated.boostersMessage) {
      return updated
    }
    // if the update changes the boostersView, clear the message
    if (state.boostersMessage) {
      if (state.boostersView !== updated.boostersView) {
        return { ...updated, boostersMessage: null }
      }
    }
    // all other updates
    return updated
  }

  const clearGoalsMessage = (
    updated: Partial<IStkViewState>,
  ): Partial<IStkViewState> => {
    if (updated.goalsMessage) {
      return updated
    }
    if (state.goalsMessage) {
      if (state.bucketsView !== updated.bucketsView) {
        return { ...updated, goalsMessage: null }
      }
    }
    return updated
  }

  const updateView = (updated: Partial<IStkViewState>): void => {
    // if there is a boostersView change
    const updatedWithBoostersMessage = updated.boostersView
      ? clearBoostersMessage(updated)
      : updated
    const updatedWithGoalsMessage = updated.bucketsView
      ? clearGoalsMessage(updated)
      : updated
    setState(prev => ({
      ...prev,
      ...updatedWithBoostersMessage,
      ...updatedWithGoalsMessage,
    }))
  }

  /**
   * Works the same as updateView except it will trigger th safetynet if
   * safetynetReady is true. Additionally the consumer must pass in a function
   * that handles focus when the the user confirms to abandon progress.
   */
  const updateViewWithSafetynet = async (
    updated: Partial<IStkViewState>,
    section: 'buckets' | 'boosters',
    proceedFocusHandler: () => void,
    tagPrefix?: string,
  ): Promise<void> => {
    if (state.safetynetReady[section]) {
      const shouldProceed = await showSafetynetModal(tagPrefix || '')
      if (shouldProceed) {
        updateView({
          ...updated,
          safetynetReady: { ...state.safetynetReady, [section]: false },
        })
        // istanbul ignore else
        if (proceedFocusHandler) {
          proceedFocusHandler()
        }
      }
    } else {
      updateView(updated)
      /* istanbul ignore else */
      if (proceedFocusHandler) {
        proceedFocusHandler()
      }
    }
  }

  /**
   * readySafetynet
   *
   * readySafetynet() sets the flag that will cause updateWithSafetynet to trigger the
   * safetynet modal. It may also be used as `readySafetynet(false)` to disable that flag
   * without using the modal (useful when a form submits, etc)
   */
  const readySafetynet = (
    section: 'buckets' | 'boosters',
    ready = true,
  ): void => {
    setState(prev => ({
      ...prev,
      safetynetReady: { ...prev.safetynetReady, [section]: ready },
    }))
  }

  // Clear the bucketsMessage, which only exists on the tiles view,
  // any time the bucketsView changes to something else.
  useEffect(() => {
    if (state.bucketsMessage && state.bucketsView !== 'TILES') {
      updateView({ bucketsMessage: null })
    }
  }, [state.bucketsView])

  return (
    <StkViewContext.Provider
      value={{
        ...state,
        updateView,
        updateViewWithSafetynet,
        readySafetynet,
        boosterType:
          state.boostersView.split('_').length > 1
            ? (state.boostersView.split('_')[0] as StkBoosterTypes)
            : null,
        refs,
        boosterTileButtonRefs,
      }}
    >
      {children}
    </StkViewContext.Provider>
  )
}

export const useStkView = (): StkViewProps => useContext(StkViewContext)
